import type { ListMethod } from '@/types/list-method';

import { EMPTY_ENRICHMENT_STATS, type EnrichmentStats } from './enrichment';
import {
  type FileImportData,
  type FileUploadState,
  initialFileUploadState,
} from './file-import.model';
import { createInitialTree, type FilterTreeState } from './filter-tree';
import { initialListImportState, type ListImportTarget } from './list-import.model';

/**
 * Which wizard state belongs to which method.
 *
 * The methods drive the wizard's *shape* (see `buildSteps`), but each one also
 * *owns* a slab of state: an uploaded file, ids pulled from another list, a
 * filter tree. Unselecting a method used to leave that slab behind, and the save
 * chain (blind to the methods) shipped it anyway (docs/product-issues.md
 * PI-40). This module is the single source of truth for that ownership, so both
 * wizards drop the same things at the same moment.
 */

/** The methods that own data. `empty` owns none; it is the absence of profiles. */
export type MethodScope = 'file' | 'import' | 'select';

/** Every data-owning scope, in wizard-step order. */
export const METHOD_SCOPES: readonly MethodScope[] = ['file', 'import', 'select'];

/** What the user loses when a scope is dropped; reads inside a sentence. */
export const METHOD_SCOPE_LABEL: Record<MethodScope, string> = {
  file: 'the uploaded file and its column mapping',
  import: 'the profiles imported from another list',
  select: 'the profile filters',
};

/** Whether each data-owning method currently holds something worth warning about. */
export type MethodScopeData = Record<MethodScope, boolean>;

/**
 * Name what unselecting these methods would throw away, as a sentence fragment:
 * "the uploaded file and its column mapping and the profile filters".
 */
export function describeScopeLoss(scopes: MethodScope[]): string {
  const labels = scopes.map((scope) => METHOD_SCOPE_LABEL[scope]);
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * The scopes abandoned when moving from one method selection to the next.
 *
 * Selecting `empty` is exclusive (`MethodCards` clears the others), so it drops
 * every scope that was active. Adding a method drops nothing.
 */
export function droppedScopes(prev: ListMethod[], next: ListMethod[]): MethodScope[] {
  return METHOD_SCOPES.filter((scope) => prev.includes(scope) && !next.includes(scope));
}

/**
 * The slice shape these transitions act on. Both wizards satisfy it
 * structurally, so a method change purges the same things in Add and Edit
 * without either slice importing the other.
 */
export interface MethodScopedTarget extends ListImportTarget {
  fileUpload: FileUploadState;
  fileImportData: FileImportData | null;
  filters: FilterTreeState;
  /** Only the *content* is method-owned; `pageSize` is a UI preference. */
  profiles: { rows: unknown[]; total: number; page: number; loading: boolean };
  duplicateCount: number;
  duplicateLoading: boolean;
  enrichment: EnrichmentStats;
  enrichNew: boolean;
  enrichStale: boolean;
}

/** Reset the state owned by one method, back to what a fresh wizard holds. */
function clearScope(state: MethodScopedTarget, scope: MethodScope): void {
  switch (scope) {
    case 'file':
      state.fileUpload = initialFileUploadState;
      state.fileImportData = null;
      return;
    case 'import':
      state.listImport = initialListImportState;
      state.importedProfileIds = [];
      state.importedListName = '';
      return;
    case 'select':
      state.filters = createInitialTree();
      state.profiles.rows = [];
      state.profiles.total = 0;
      state.profiles.page = 0;
      state.profiles.loading = false;
      state.duplicateCount = 0;
      state.duplicateLoading = false;
  }
}

/**
 * Apply a method change: drop everything the unselected methods owned.
 *
 * The enrichment buckets are derived from the *union* of all three sources, so
 * losing any one of them invalidates them, along with the two toggles that
 * target their ids. `ConfirmStep` recomputes the buckets when it mounts.
 *
 * Returns the scopes that were dropped, so a caller can report them.
 */
export function applyMethodsChanged(
  state: MethodScopedTarget,
  prev: ListMethod[],
  next: ListMethod[],
): MethodScope[] {
  const dropped = droppedScopes(prev, next);
  for (const scope of dropped) clearScope(state, scope);
  if (dropped.length > 0) {
    state.enrichment = EMPTY_ENRICHMENT_STATS;
    state.enrichNew = false;
    state.enrichStale = false;
  }
  return dropped;
}
